import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  apiFetchPins,
  apiPinEntity,
  apiUnpinEntity,
  type PinEntityType,
  type PinItem,
} from "../../lib/authApi";
import { useAuth } from "./AuthContext";

export type PinsContextValue = {
  pins: PinItem[];
  isPinned: (entityType: PinEntityType, entityId: string) => boolean;
  getPinnedAt: (entityType: PinEntityType, entityId: string) => number | null;
  togglePin: (entityType: PinEntityType, entityId: string) => void;
};

const PinsContext = createContext<PinsContextValue | null>(null);

export function usePins(): PinsContextValue {
  const ctx = useContext(PinsContext);
  if (!ctx) throw new Error("usePins must be used within PinsProvider");
  return ctx;
}

function pinKey(entityType: string, entityId: string): string {
  return `${entityType}:${entityId}`;
}

export function PinsProvider({ children }: { children: ReactNode }) {
  const { identityRow } = useAuth();
  const userId = identityRow?.userId ?? null;
  const [pins, setPins] = useState<PinItem[]>([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!userId) {
      setPins([]);
      return;
    }
    let cancelled = false;
    apiFetchPins()
      .then((items) => {
        if (!cancelled && mountedRef.current) setPins(items);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userId]);

  const isPinned = useCallback(
    (entityType: PinEntityType, entityId: string): boolean =>
      pins.some((p) => p.entity_type === entityType && p.entity_id === entityId),
    [pins],
  );

  const getPinnedAt = useCallback(
    (entityType: PinEntityType, entityId: string): number | null => {
      const p = pins.find((p) => p.entity_type === entityType && p.entity_id === entityId);
      return p ? p.pinned_at : null;
    },
    [pins],
  );

  const togglePin = useCallback(
    (entityType: PinEntityType, entityId: string) => {
      const key = pinKey(entityType, entityId);
      const existing = pins.find((p) => pinKey(p.entity_type, p.entity_id) === key);

      if (existing) {
        setPins((prev) => prev.filter((p) => pinKey(p.entity_type, p.entity_id) !== key));
        apiUnpinEntity(entityType, entityId).catch(() => {
          if (mountedRef.current) {
            setPins((prev) => [...prev, existing]);
          }
        });
      } else {
        const optimistic: PinItem = {
          entity_type: entityType,
          entity_id: entityId,
          pinned_at: Date.now(),
        };
        setPins((prev) => [...prev, optimistic]);
        apiPinEntity(entityType, entityId)
          .then((real) => {
            if (mountedRef.current) {
              setPins((prev) =>
                prev.map((p) =>
                  pinKey(p.entity_type, p.entity_id) === key ? { ...p, pinned_at: real.pinned_at } : p,
                ),
              );
            }
          })
          .catch(() => {
            if (mountedRef.current) {
              setPins((prev) => prev.filter((p) => pinKey(p.entity_type, p.entity_id) !== key));
            }
          });
      }
    },
    [pins],
  );

  const value: PinsContextValue = { pins, isPinned, getPinnedAt, togglePin };

  return <PinsContext.Provider value={value}>{children}</PinsContext.Provider>;
}
